import useNotification from "utils/hooks/useNotification";
import useProductApi from "../../hooks/useProductApi";
import { ApolloQueryResult } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Button, Form, Input } from "antd";
import { useState } from "react";
import React from "react";
import { HiPencil } from "react-icons/hi";

interface ProductMarginPropsTypes {
  name: number;
  refetchProduct?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetProductResponse>>;
  refetchProducts?: () => void;
}

// Frontend-only field. Margin is computed over the selling price (Prix HT):
//   margin% = (priceNet - buyPriceTotal) / priceNet * 100
// Editing recomputes priceNet = buyPriceTotal / (1 - margin/100) and persists it.
export default function ProductMargin(props: ProductMarginPropsTypes): React.ReactElement {
  const { name, refetchProduct, refetchProducts } = props;
  const { t } = useTranslation();
  const form = Form.useFormInstance();

  const [editMargin, setEditMargin] = useState<boolean>(false);
  const [marginInput, setMarginInput] = useState<string>("");

  const { notificationError, notificationSuccessfullySaved } = useNotification();
  const { updateProductPrice } = useProductApi();

  const extraInfo = Form.useWatch(["prices", name, "extraInfo"], form);
  const currency = form.getFieldValue(["prices", name, "currency"]);
  const buyPriceTotal = Form.useWatch(["prices", name, "buyPriceTotal"], form);
  const priceNet = Form.useWatch(["prices", name, "priceNet"], form);
  const productId = Form.useWatch(["productId"], form);

  const decimalPlaces = extraInfo?.decimalPlaces ?? 2;

  const buy = Number(buyPriceTotal);
  const net = Number(priceNet);
  const hasValues =
    buyPriceTotal !== undefined &&
    buyPriceTotal !== null &&
    buyPriceTotal !== "" &&
    priceNet !== undefined &&
    priceNet !== null &&
    priceNet !== "" &&
    !isNaN(buy) &&
    !isNaN(net) &&
    net !== 0;

  const computedMargin = hasValues ? ((net - buy) / net) * 100 : undefined;

  const formattedMargin =
    computedMargin !== undefined
      ? `${computedMargin.toLocaleString(undefined, {
          minimumFractionDigits: decimalPlaces,
          maximumFractionDigits: decimalPlaces,
        })} %`
      : "—";

  const enterEdit = () => {
    setMarginInput(
      computedMargin !== undefined ? Number(computedMargin.toFixed(decimalPlaces)).toString() : "",
    );
    setEditMargin(true);
  };

  const cancelEdit = () => {
    setEditMargin(false);
  };

  const handleOnFinish = async () => {
    const marginValue = Number(marginInput);

    if (isNaN(marginValue) || buyPriceTotal === undefined || isNaN(buy)) {
      setEditMargin(false);
      return;
    }

    // Margin is over selling price: m = (net - buy) / net  =>  net = buy / (1 - m/100)
    const divisor = 1 - marginValue / 100;
    if (divisor <= 0) {
      setEditMargin(false);
      return;
    }
    const newNet = Number((buy / divisor).toFixed(decimalPlaces));

    // Reflect immediately in the form (frontend).
    form.setFieldValue(["prices", name, "priceNet"], newNet);

    const variables = {
      input: {
        product: productId,
        net: newNet,
        currency,
      },
    };

    const response = await updateProductPrice({ variables });
    const errors = response?.data?.updateProductPrice?.errors;

    if ((errors?.length ?? 0) > 0) {
      notificationError(errors);
      setEditMargin(true);
    } else {
      notificationSuccessfullySaved(t("Product price"));
      setEditMargin(false);
      refetchProduct!();
      refetchProducts?.();
    }
  };

  const isEditing = editMargin || !productId;

  if (productId && !isEditing) {
    return (
      <div
        role="button"
        tabIndex={0}
        className="update-product-price__display"
        onClick={enterEdit}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            enterEdit();
          }
        }}
      >
        <span className="update-product-price__value">{formattedMargin}</span>
        <HiPencil size={16} className="update-product-price__pencil" />
      </div>
    );
  }

  return (
    <div className="update-product-price__editor">
      <div className="update-product-price__field">
        <Input
          data-hj-allow
          className="text-align-right"
          maxLength={15}
          suffix="%"
          value={marginInput}
          onFocus={e => e.target.select()}
          onChange={e => {
            const v = e.target.value;
            if (/^-?\d*\.?\d*$/.test(v)) setMarginInput(v);
          }}
          onPressEnter={handleOnFinish}
        />
      </div>
      {productId && (
        <div className="update-product-price__actions">
          <Button size="small" onClick={cancelEdit}>
            {t("Cancel")}
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleOnFinish}
            disabled={marginInput === "" || buyPriceTotal === undefined}
          >
            {t("Save short")}
          </Button>
        </div>
      )}
    </div>
  );
}
